import { useDispatch } from 'react-redux';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { reportIssueSuccessMessage } from 'dashboard/lib/constants/reportIssue';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { DocumentGeneric } from 'dashboard/models/document';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { Verification } from 'dashboard/models/verification';
import ResourceInquiry from 'dashboard/modules/ResourceInquiry';
import { ModuleResourceInquiryV2 } from 'dashboard/modules/ResourceInquiryV2/model';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useGetInquiryObjectByIdQuery } from 'dashboard/redux/api/account';
import {
  useFlagInquiryIssuesMutation,
  useUpdateInquiryTagsMutation,
} from 'dashboard/redux/api/inquiry';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

export interface ResourceInquiryV2Props {
  module: ModuleResourceInquiryV2;
  currentObjectToken: string;
  inquiryToken: string;
  featureFlags: FeatureFlagsMap;
}

const ResourceInquiryV2 = ({
  currentObjectToken,
  inquiryToken,
  module,
  featureFlags,
}: ResourceInquiryV2Props) => {
  const dispatch = useDispatch();

  const { data, error, isLoading, refetch } = useGetInquiryObjectByIdQuery({
    id: currentObjectToken,
    inquiryId: inquiryToken,
  });

  useRTKErrorHandler({
    error,
    onError: () => {
      return {
        message: 'Failed to load Inquiry.',
        level: MessageType.Failure,
      };
    },
  });

  const [onFlagInquiryIssues, { error: flagInquiryIssuesError }] = useFlagInquiryIssuesMutation();

  useRTKErrorHandler({
    error: flagInquiryIssuesError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  const handleFlagInquiryIssues = async (
    inquiry: Inquiry,
    verifications: Verification[],
    values: FlagIssuesFormData,
  ) => {
    await onFlagInquiryIssues({ id: inquiry.id, verifications, ...values });

    dispatch(showMessage(reportIssueSuccessMessage(values.responseType), MessageType.Success));
  };

  const [onUpdateInquiryTags, { error: updateInquiryTagsError }] = useUpdateInquiryTagsMutation();

  const handleUpdateInquiryTags = async (id: string, tags: string[]) => {
    await onUpdateInquiryTags({ id, tags });
    await refetch();
  };

  useRTKErrorHandler({
    error: updateInquiryTagsError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  if (isLoading || !data) {
    return <LoadingIndicator />;
  }

  const inquiryResponse = data.data;
  const inquiryDocuments = data.included?.filter(
    (object) => object.type === ApiModel.DocumentGeneric,
  ) as DocumentGeneric[];
  const inquiryVerifications = data.included?.filter(
    (object) => object.type === ApiModel.Verification,
  ) as Verification[];
  const inquiryTemplate = data.included?.find(
    (object) => object.type === ApiModel.InquiryTemplate || object.type === ApiModel.Template,
  ) as InquiryTemplate | Template;
  const inquiryTemplateVersion = data.included?.find(
    (object) => object.type === ApiModel.InquiryTemplateVersion,
  ) as InquiryShowViewInquiryTemplateVersion;
  const inquiryTags = data.included?.filter((object) => object.type === ApiModel.Tag) as Tag[];

  // Filter on inquiry template if specified
  if (module['inquiry-template'] && inquiryTemplate?.id !== module['inquiry-template']) {
    return null;
  }

  return (
    <ResourceInquiry
      attributes={module.attributes}
      hideEmptyAttributes={module['hide-empty-attributes']}
      inquiry={inquiryResponse}
      documents={inquiryDocuments}
      tags={inquiryTags}
      inquiryTemplates={inquiryTemplate ? [inquiryTemplate] : []}
      inquiryTemplateVersions={inquiryTemplateVersion ? [inquiryTemplateVersion] : []}
      verifications={inquiryVerifications}
      defaultAccordionOpen={module['default-accordion-open'] ?? true}
      featureFlags={featureFlags}
      handleUpdateInquiryTags={handleUpdateInquiryTags}
      handleFlagInquiryIssues={handleFlagInquiryIssues}
    />
  );
};

export default ResourceInquiryV2;
